<template>
  <div v-if="(this.markers && this.markers.length && this.markers[0]) || this.noEmptyMessage">
    <div id="osm"></div>
  </div>
  <div v-else class="text-center">
    No coordinates available
  </div>
</template>

<script>
  import api from '../../api'
  import moment from 'moment'
  import helper from '../../helper'

  const LEAFLET_URL = '/static/js/plugins/leaflet/leaflet.js'
  const LEAFLET_AREA_URL = '/static/js/plugins/leaflet/leaflet-areaselect.js'
  const LEAFLET_SEARCH_URL = '/static/js/plugins/leaflet/leaflet-search.js'
  const DOM_TARGET_ID = 'osm'

  let L
  let map

  export default {
    props: ['markers', 'search', 'filters', 'noEmptyMessage'],
    data () {
      return {
        url: 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png',
        attribution: '&copy; <a href="http://osm.org/copyright">OpenStreetMap</a> contributors',
        lMarkers: [],
        cMarkers: [],
        startDate: null,
        endDate: null
      }
    },
    mounted () {
      this.$nextTick(() => {
        this.initMap()
      })
    },
    methods: {
      initMap () {
        const that = this

        window.jQuery.getScript(LEAFLET_URL, () => {
          window.jQuery.getScript(LEAFLET_AREA_URL, () => {
            window.jQuery.getScript(LEAFLET_SEARCH_URL, () => {
              L = window.L

              if (!L.DomUtil.get(DOM_TARGET_ID)) {
                return
              }

              // Build map
              map = L.map(DOM_TARGET_ID, {
                maxBounds: L.latLngBounds([[-90, -360], [90, 360]]),
                maxBoundsViscosity: 1.0,
                worldCopyJump: true,
                zoomControl: false,
                minZoom: 1
              })

              L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
                attribution: '&copy; <a href="http://osm.org/copyright">OpenStreetMap</a> contributors',
                zoom: 18
              }).addTo(map)

              // Add custom 'filters' control
              if (this.filters) {
                this.addFiltersControl(L, map, this.filterTestimonies)
              }

              // Add native 'zoom' control
              map.addControl(L.control.zoom({position: 'topleft'}))

              if (this.search) {
                map.addControl(new L.Control.Search({
                  url: `https://nominatim.openstreetmap.org/search?q={s}&format=json&addressdetails=0&language=english`,
                  textPlaceholder: 'Search place...',
                  position: 'topleft',
                  propertyName: 'display_name',
                  propertyLoc: ['lat', 'lon'],
                  marker: {
                    icon: new L.Icon({iconUrl: '/static/css/leaflet/images/marker-icon-red.png'}),
                    circle: null
                  },
                  hideMarkerOnCollapse: true,
                  autoType: false,
                  minLength: 3,
                  zoom: 12
                }))
              }

              // Add Wikimedia i18n raster tiles: https://wiki.openstreetmap.org/wiki/Tile_servers
              L.tileLayer('https://maps.wikimedia.org/osm-intl/{z}/{x}/{y}.png?lang=en', {
                'id': 'wikimedia-tiles',
                'type': 'raster',
                'tileSize': 256
              }).addTo(map)

              if (that.markers && that.markers.length) {
                this.addRelocateControl(L, map, this.fitToMarkers)

                // Add map markers
                that.markers.forEach(marker => {
                  const lMarker = L.marker(L.latLng(marker.coordinates.latitude, marker.coordinates.longitude), {
                    title: marker.title || ''
                  })

                  lMarker.addTo(map)
                  that.lMarkers.push(lMarker) // keep marker reference for future operation

                  if (marker.perimeter) {
                    const cMarker = L.circle(L.latLng(marker.coordinates.latitude, marker.coordinates.longitude), {
                      color: '#2070b7',
                      opacity: 0.7,
                      weight: 2,
                      fillColor: '#2b82cb',
                      fillOpacity: 0.2,
                      radius: marker.perimeter
                    })
                    cMarker.addTo(map)
                    that.cMarkers.push(cMarker) // keep marker reference for future operation
                  }

                  if (marker.popup) {
                    let popupContent = marker.popup
                    if (marker.link) {
                      window.goTo = function (context) {
                        const route = {
                          name: context.getAttribute('data-route'),
                          params: {}
                        }

                        context.getAttributeNames().filter(attr => attr.startsWith('data-params-')).forEach(paramAttribute => {
                          route.params[paramAttribute.substring('data-params-'.length)] = context.getAttribute(paramAttribute)
                        })

                        that.$router.push(route)
                      }

                      const linkButton = L.DomUtil.create('button', 'btn btn-xs btn-block btn-link')
                      linkButton.type = 'button'
                      linkButton.innerHTML = marker.link.label
                      linkButton.setAttribute('onclick', 'goTo(this)')
                      linkButton.setAttribute('data-route', marker.link.route.name)
                      for (const param in marker.link.route.params) {
                        if (marker.link.route.params.hasOwnProperty(param)) {
                          linkButton.setAttribute('data-params-' + param, marker.link.route.params[param])
                        }
                      }

                      popupContent += `<br>${linkButton.outerHTML}`
                    }
                    lMarker.bindPopup(popupContent)
                  }
                })

                this.fitToMarkers()
              } else {
                map.setView(L.latLng(35, 0), 3)
              }
            })
          })
        })
      },
      addFiltersControl (L, map, callback) {
        L.Control.Filter = L.Control.extend({
          onAdd: function (map) {
            // const $ = window.jQuery
            const div = L.DomUtil.create('div', 'leaflet-control-filters leaflet-bar')

            const fromContainer = document.createElement('div')
            fromContainer.className = 'filter from-date'
            const fromLabel = document.createElement('label')
            fromLabel.textContent = 'From'
            const fromInput = document.createElement('input')
            fromInput.className = 'form-control'
            fromInput.type = 'date'

            fromContainer.appendChild(fromLabel)
            fromContainer.appendChild(fromInput)

            const toContainer = document.createElement('div')
            toContainer.className = 'filter to-date'
            const toLabel = document.createElement('label')
            toLabel.textContent = 'To'
            const toInput = document.createElement('input')
            toInput.className = 'form-control'
            toInput.type = 'date'

            toContainer.appendChild(toLabel)
            toContainer.appendChild(toInput)

            const searchButton = document.createElement('button')
            searchButton.className = 'btn btn-primary'
            searchButton.type = 'button'
            searchButton.innerHTML = '<i class="fa fa-search"></i>'

            div.appendChild(fromContainer)
            div.appendChild(toContainer)
            div.appendChild(searchButton)

            L.DomEvent
              .addListener(searchButton, 'click', L.DomEvent.stopPropagation)
              .addListener(searchButton, 'click', L.DomEvent.preventDefault)
              .addListener(searchButton, 'click', function () {
                callback({
                  startDate: fromInput.value,
                  endDate: toInput.value
                })
              })

            return div
          },

          onRemove: function (map) {
            // Nothing to do here
          }
        })
        L.control.filter = function (opts) {
          return new L.Control.Filter(opts)
        }
        L.control.filter({position: 'topleft'}).addTo(map)
      },
      addRelocateControl (L, map, callback) {
        var markerIcon = this.search ? 'fa-globe' : 'fa-map-marker-alt'

        L.Control.Relocate = L.Control.extend({
          onAdd: function () {
            const div = L.DomUtil.create('div', 'leaflet-bar')
            L.DomUtil.create('a', `leaflet-control-rencenter fa ${markerIcon} fa-lg`, div)

            L.DomEvent
              .addListener(div, 'click', L.DomEvent.stopPropagation)
              .addListener(div, 'click', L.DomEvent.preventDefault)
              .addListener(div, 'click', callback)

            return div
          },

          onRemove: function (map) {
            // Nothing to do here
          }
        })

        const control = new L.Control.Relocate({position: 'topleft'})
        control.addTo(map)
      },
      filterTestimonies (data) {
        const that = this
        const params = []

        const startMoment = moment(data.startDate, 'YYYY-MM-DD')
        if (startMoment.isValid()) {
          params.push('startDate=' + startMoment.format('MM/DD/YYYY'))
        }

        const endMoment = moment(data.endDate, 'YYYY-MM-DD')
        if (endMoment.isValid()) {
          params.push('endDate=' + endMoment.format('MM/DD/YYYY'))
        }

        const query = (params.length) ? `?${params.join('&')}` : ''

        api.authenticatedRequest('GET', `/testimonies/all${query}`).then(response => {
          const testimonies = response.data

          // Clear all current map markers
          for (let i = 0; i < that.lMarkers.length; i++) {
            map.removeLayer(that.lMarkers[i])
          }
          that.lMarkers = []
          // Clear all current circle markers
          for (let i = 0; i < that.cMarkers.length; i++) {
            map.removeLayer(that.cMarkers[i])
          }
          that.cMarkers = []

          // Add markers from search
          console.log(testimonies)
          const searchMarkers = helper.extractTestimoniesMarkers(testimonies.data, true)
          console.log(searchMarkers)

          searchMarkers.forEach(marker => {
            const lMarker = L.marker(L.latLng(marker.coordinates.latitude, marker.coordinates.longitude), {
              title: marker.title || ''
            })

            lMarker.addTo(map)
            that.lMarkers.push(lMarker) // keep marker reference for future operation

            if (marker.perimeter) {
              const cMarker = L.circle(L.latLng(marker.coordinates.latitude, marker.coordinates.longitude), {
                color: '#2070b7',
                opacity: 0.5,
                weight: 2,
                fillColor: '#2b82cb',
                fillOpacity: 0.1,
                radius: marker.perimeter
              })
              cMarker.addTo(map)
              that.cMarkers.push(cMarker) // keep marker reference for future operation
            }

            if (marker.popup) {
              let popupContent = marker.popup
              if (marker.link) {
                window.goTo = function (context) {
                  const route = {
                    name: context.getAttribute('data-route'),
                    params: {}
                  }

                  context.getAttributeNames().filter(attr => attr.startsWith('data-params-')).forEach(paramAttribute => {
                    route.params[paramAttribute.substring('data-params-'.length)] = context.getAttribute(paramAttribute)
                  })

                  that.$router.push(route)
                }

                const linkButton = L.DomUtil.create('button', 'btn btn-xs btn-block btn-link')
                linkButton.type = 'button'
                linkButton.innerHTML = marker.link.label
                linkButton.setAttribute('onclick', 'goTo(this)')
                linkButton.setAttribute('data-route', marker.link.route.name)
                for (const param in marker.link.route.params) {
                  if (marker.link.route.params.hasOwnProperty(param)) {
                    linkButton.setAttribute('data-params-' + param, marker.link.route.params[param])
                  }
                }

                popupContent += `<br>${linkButton.outerHTML}`
              }
              lMarker.bindPopup(popupContent)
            }
          })
        })
      },
      fitToMarkers () {
        if (!map || !this.markers || !this.markers.length) {
          return
        }

        let latLngs
        if (this.markers.length > 1) {
          // Multiple markers
          latLngs = this.markers.map(marker => [marker.coordinates.latitude, marker.coordinates.longitude])
        } else if (this.markers.length === 1) {
          // Single marker, duplicate it to have two points with opposite slight offsets
          const coord = this.markers[0].coordinates
          const offset = 0.25
          latLngs = [
            L.latLng(coord.latitude + offset, coord.longitude + offset),
            L.latLng(coord.latitude - offset, coord.longitude - offset)
          ]
        }

        const latLngBounds = L.latLngBounds(latLngs).pad(0.1)
        map.fitBounds(latLngBounds)
      }
    }
  }
</script>

<style>
  @import "/static/css/leaflet/leaflet.css";
  @import "/static/css/leaflet/leaflet-areaselect.css";
  @import "/static/css/leaflet/leaflet-search.css";

  #osm {
    height: 400px;
  }

  .leaflet-control > a {
    cursor: pointer;
  }

  .leaflet-control-filters {
    display: flex;
    background-color: #fff;
  }

  .leaflet-control-filters > .filter {
    display: flex;
    align-items: baseline;
    align-self: center;
  }

  .leaflet-control-filters > .filter > label {
    padding: 0 1em;
  }

  .leaflet-control-filters > .filter > input[type=date] {
    border: none;
  }

  .leaflet-control-filters > button {
    border-radius: 0;
  }
  .search-button:before {
    font-size: 18px;
    color: black;
    display: flex;
    height: 100%;
    justify-content: center;
    align-items: center;
  }
</style>



// WEBPACK FOOTER //
// Map.vue?2d322190